import { createGlobalState, useStorage } from '@vueuse/core';
import { format } from 'date-fns';
import type { StreakStatus, StreakDetails, StreakAvailability, WeeklyGoal } from '~/types/streak.types';
import { STREAK_FEATURE_OFF, useStreaks } from '~/composables/useStreaks';

// device-local day, intentionally not the backend's X-User-Timezone day —
// this only dedupes the toast per calendar day on this device
function localDateKey (): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export const useStreakStore = createGlobalState(() => {
  const { getStatus, getDetails, updateGoal } = useStreaks();

  const state = reactive<{
    status: StreakStatus | null;
    details: StreakDetails | null;
    availability: StreakAvailability;
    isStatusLoading: boolean;
    isDetailsLoading: boolean;
  }>({
    status: null,
    details: null,
    availability: 'unknown',
    isStatusLoading: false,
    isDetailsLoading: false,
  });

  const toastShownDate = useStorage<string>('streak_toast_shown_date', '');

  const status = computed(() => state.status);
  const details = computed(() => state.details);
  const availability = computed(() => state.availability);
  const isLoading = computed(() => state.isStatusLoading || state.isDetailsLoading);

  const toastSummary = (key: string): string =>
    useNuxtApp().$i18n?.t(`streak.errors.${key}`) ?? `streak.errors.${key}`;

  let statusRequest: Promise<void> | null = null;
  let detailsRequest: Promise<boolean> | null = null;

  const loadStatus = (): Promise<void> => {
    if (statusRequest) {
      return statusRequest;
    }
    statusRequest = (async () => {
      state.isStatusLoading = true;
      try {
        const result = await useErrorHandling(getStatus, toastSummary('loadStatusFailed'));
        if (result === undefined) {
          state.availability = 'error';
          state.status = null;
        } else if (result === STREAK_FEATURE_OFF) {
          state.availability = 'off';
          state.status = null;
        } else {
          state.availability = 'on';
          state.status = result;
        }
      } finally {
        state.isStatusLoading = false;
        statusRequest = null;
      }
    })();
    return statusRequest;
  };

  const loadDetails = (): Promise<boolean> => {
    if (detailsRequest) {
      return detailsRequest;
    }
    detailsRequest = (async () => {
      state.isDetailsLoading = true;
      try {
        const result = await useErrorHandling(getDetails, toastSummary('loadDetailsFailed'));
        if (!result) {
          return false;
        }
        state.details = result;
        return true;
      } finally {
        state.isDetailsLoading = false;
        detailsRequest = null;
      }
    })();
    return detailsRequest;
  };

  const saveGoal = async (weeklyGoal: WeeklyGoal): Promise<boolean> => {
    const result = await useErrorHandling(() => updateGoal(weeklyGoal), toastSummary('saveGoalFailed'));
    if (!result) {
      return false;
    }
    state.details = result;
    return true;
  };

  const wasToastShownToday = () => toastShownDate.value === localDateKey();
  const markToastShownToday = () => { toastShownDate.value = localDateKey(); };

  const reset = () => {
    state.status = null;
    state.details = null;
    state.availability = 'unknown';
    state.isStatusLoading = false;
    state.isDetailsLoading = false;
    toastShownDate.value = '';
  };

  return {
    // getters (readonly)
    status,
    details,
    availability,
    isLoading,

    // actions
    loadStatus,
    loadDetails,
    saveGoal,
    wasToastShownToday,
    markToastShownToday,
    reset,
  };
});
